import {
  Checkbox,
  FormInput,
  FormInputNumber,
  FormSelect,
  PrimaryButton,
  RadioGroup,
  RadioGroupDataInterface,
  SecondaryButton,
  Units,
  cents,
  centsToUnits,
  units,
} from '@systemeio/ui'
import { TaxBehaviorEnum, taxBehaviorLocoKeys } from 'modules/payment/tax-behavior'
import { WEIGHT_STEP } from 'modules/profile/shipping/constants/shipping-consts'
import { useEffect, useState } from 'react'
import FormTextarea from 'shared/components/form-textarea'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useCurrencyData } from 'shared/hooks/use-currency-data'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { emptyProductErrorsState } from '../constants/product-errors'
import ProductErrorsInterface from '../types/error-response-content-interface'
import {
  ProductDataInterface,
  ProductInterface,
  ProductSaveInterface,
} from '../types/product-interface'
import { ProductVariantsErrorsInterface } from '../types/product-variant-interface'
import { filterOptions, filterVariants, validateVariants } from '../utils/product-filters'
import { getVariants } from '../utils/product-variants-utils'
import Options from './Options'
import Variants from './Variants'
import ProductBreadcrumbs from './product-breadcrumbs'
import MediaLibrary from './product-media-library'

const defaultNumberValue = '' as unknown as number

interface ProductTemplateProps {
  product?: ProductInterface
  maxProductVariants: number
  validationErrorsNotification?: boolean
  setValidationErrorsNotification: React.Dispatch<React.SetStateAction<boolean>>
  onSave: (saveProductData: ProductSaveInterface) => void
  isFetching?: boolean
  defaultData?: Pick<ProductInterface, 'currency' | 'tax'> | undefined
}

const ProductForm = ({
  product,
  maxProductVariants,
  validationErrorsNotification,
  onSave,
  isFetching,
  setValidationErrorsNotification,
  defaultData,
}: ProductTemplateProps) => {
  const { t } = useLocoTranslation()

  const [name, setName] = useState(product?.name ?? '')
  const [description, setDescription] = useState(product?.description ?? '')
  const [sku, setSku] = useState(product?.sku ?? '')
  const [tax, setTax] = useState(product?.tax ?? defaultNumberValue)
  const [weight, setWeight] = useState(product?.weight ?? defaultNumberValue)
  const [price, setPrice] = useState(
    product?.price ? centsToUnits(product.price) : units(defaultNumberValue),
  )
  const [currencyType, setCurrencyType] = useState(product?.currency)
  const [limitStock, setLimitStock] = useState(product?.limitStock ?? false)
  const [shippingFeesDisabled, setShippingFeesDisabled] = useState(
    product?.shippingFeesDisabled ?? false,
  )
  const [taxBehavior, setTaxBehavior] = useState(product?.taxBehavior ?? TaxBehaviorEnum.Exclusive)
  const [stock, setStock] = useState(product?.stock ?? defaultNumberValue)
  const [images, setImages] = useState(product?.images ?? [])
  const [options, setOptions] = useState(product?.options ?? [])
  const [variants, setVariants] = useState(product?.productVariants ?? [])

  const [errors, setErrors] = useState(emptyProductErrorsState)
  const [productVariantsErrors, setProductVariantsErrors] =
    useState<ProductVariantsErrorsInterface>({})

  const currencyData = useCurrencyData()

  const handleSave = () => {
    const filteredOptions = filterOptions(options)
    const filteredVariants = filterVariants(variants)

    const areValidVariants = validateVariants(filteredVariants, t, setProductVariantsErrors)
    if (!areValidVariants) return
    if (!currencyType) return

    const productData: ProductDataInterface = {
      name: name,
      description: description,
      tax: tax,
      sku: sku,
      images: images,
      currency: currencyType,
      price: price ? cents((price * 100).toFixed(0)) : cents(price),
      weight: weight,
      options: filteredOptions,
      productVariants: filteredVariants,
      limitStock: limitStock,
      shippingFeesDisabled: shippingFeesDisabled,
      taxBehavior: taxBehavior,
    }

    if (limitStock) {
      productData.stock = stock || 0
    }

    onSave?.({
      productData,
      setErrors,
      setVariantErrors: setProductVariantsErrors,
    })
  }

  useEffect(() => {
    setVariants(prev => {
      const newVariants = getVariants(options, prev)

      if (!limitStock && !stock) return newVariants

      return newVariants.map(v => ({ ...v, stock: v.stock ?? stock ?? 0 }))
    })
  }, [options])

  useEffect(() => {
    if (!product && defaultData) {
      if (tax === defaultNumberValue) setTax(defaultData.tax)
      if (!currencyType) setCurrencyType(defaultData.currency)
    }
  }, [currencyType, defaultData, tax, product])

  useEffect(() => {
    let isErrors = false
    Object.keys(errors).forEach(el => {
      if (el === 'common') return
      if (el === 'options') {
        if (!!Object.keys(errors[el as keyof ProductErrorsInterface]).length) isErrors = true
        return
      }
      if (!!errors[el as keyof ProductErrorsInterface]) isErrors = true
    })
    setValidationErrorsNotification(isErrors)
  }, [errors])

  const taxBehaviorOptions: RadioGroupDataInterface<TaxBehaviorEnum>[] = [
    {
      id: TaxBehaviorEnum.Exclusive,
      caption: t(taxBehaviorLocoKeys[TaxBehaviorEnum.Exclusive]),
    },
    {
      id: TaxBehaviorEnum.Inclusive,
      caption: t(taxBehaviorLocoKeys[TaxBehaviorEnum.Inclusive]),
    },
  ]

  return (
    <div className="2xl:1/2 m-auto max-w-[900px] xl:w-5/6">
      {product && <ProductBreadcrumbs />}
      <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">
        <div className="text-text-primary mb-2 text-lg font-semibold">
          {t('dashboard.products.name.title')}
        </div>
        <FormInput
          value={name}
          placeholder={t('dashboard.products.name.title')}
          onChange={e => {
            setErrors(prev => {
              return { ...prev, name: '' }
            })
            setName(e.target.value)
          }}
          maxLength={256}
          error={errors.name}
        />
        <div className="lg:my-6">
          <div className="text-text-primary mb-2 text-lg font-semibold">
            {t('dashboard.products.description.title')}
          </div>
          <FormTextarea
            placeholder={t('dashboard.products.description.title')}
            value={description ?? ''}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
        <div className="lg:my-6">
          <div className="flex flex-col justify-center lg:flex-row lg:gap-8">
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('dashboard.products.sku.title')}
              </div>
              <FormInput
                value={sku}
                placeholder={t('dashboard.products.sku.title')}
                onChange={e => setSku(e.target.value)}
                onFocus={() => {
                  if (!sku && name) {
                    const calculatedSku = `${name.substring(0, 3).toUpperCase()}-${Math.floor(
                      Math.random() * 1000,
                    )}`
                    setErrors(prev => {
                      return { ...prev, sku: '' }
                    })
                    setSku(calculatedSku)
                  }
                }}
                maxLength={256}
                error={errors.sku}
              />
            </div>
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('dashboard.products.list.tax_header')}
              </div>
              <FormInputNumber
                value={tax}
                placeholder={t('dashboard.products.list.tax_header')}
                onChange={value => {
                  setErrors(prev => {
                    return { ...prev, tax: '' }
                  })
                  setTax(value)
                }}
                toFixed={1}
                min={0}
                max={99}
                error={errors.tax}
              />
            </div>
          </div>
        </div>
        <div className="lg:my-6">
          <div className="flex flex-col justify-center lg:flex-row lg:gap-8">
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('dashboard.payment.tax_behavior.title')}
              </div>
              <RadioGroup
                options={taxBehaviorOptions}
                selected={taxBehavior}
                onChange={value => setTaxBehavior(value)}
                error={errors.taxBehavior}
              />
            </div>
          </div>
        </div>
        <div className="lg:my-6">
          <div className="flex flex-col justify-center lg:flex-row lg:gap-8">
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('global.currency')}
              </div>
              <FormSelect
                onChange={value => {
                  setErrors(prev => {
                    return { ...prev, currency: '' }
                  })
                  setCurrencyType(value)
                }}
                value={currencyType}
                data={currencyData}
                placeholder={t('dashboard.product.currency_select')}
                error={errors.currency}
                withoutCloseIcon
              />
              {product && product.currency !== currencyType && (
                <p className="text-yellow mt-2 text-sm">{t('product.currency.alert_message')}</p>
              )}
            </div>
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('global.price')}
              </div>
              <FormInputNumber
                value={price}
                placeholder={t('global.price')}
                onChange={value => {
                  setErrors(prev => {
                    return { ...prev, price: '' }
                  })
                  setPrice(value as Units)
                }}
                min={0}
                error={errors.price}
              />
            </div>
          </div>
        </div>
        <div className="lg:my-6">
          <div className="flex flex-col justify-center lg:flex-row lg:gap-8">
            <div className="flex w-full flex-col">
              <div className="text-text-primary my-2 text-lg font-semibold">
                {t('dashboard.products.weight.title')}
              </div>
              <FormInputNumber
                value={weight}
                placeholder={t('dashboard.products.weight.title')}
                onChange={value => {
                  setErrors(prev => {
                    return { ...prev, weight: '' }
                  })
                  setWeight(value)
                }}
                min={0}
                addonAfter={t('dashboard.products.weight.gram_short')}
                step={WEIGHT_STEP}
                error={errors.weight}
              />
            </div>
          </div>
        </div>
        <div className="lg:my-6">
          <div className="flex flex-col justify-center lg:flex-row lg:gap-8">
            <div className="my-2 flex w-full flex-col">
              <div className="text-text-primary mb-2 text-lg font-semibold">
                {t('dashboard.products.stock.title')}
              </div>
              <div className="mt-4">
                <Checkbox
                  value={limitStock}
                  onChange={setLimitStock}
                  label={t('dashboard.products.allow_stock_description')}
                />
              </div>
            </div>
            <div className="mb-2 mt-11 flex w-full flex-col">
              <FormInputNumber
                value={stock ?? 0}
                placeholder={t('dashboard.products.stock.placeholder')}
                onChange={value => {
                  setErrors(prev => {
                    return { ...prev, stock: '' }
                  })
                  setStock(value)
                }}
                error={errors.stock}
                disabled={!limitStock || variants.length > 0}
                type={'integer'}
              />
            </div>
          </div>
          <div className="my-2 flex w-full flex-col">
            <div className="text-text-primary mb-2 text-lg font-semibold">
              {t('dashboard.products.shipping.title')}
            </div>
            <div className="mt-4">
              <Checkbox
                error={errors.shippingFeesDisabled}
                value={shippingFeesDisabled}
                onChange={setShippingFeesDisabled}
                label={t('dashboard.products.shipping.disable_fees')}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">
        <div className="text-text-primary mb-2 text-lg font-semibold">
          {t('dashboard.products.media.title')}
        </div>
        <MediaLibrary images={images} setImages={setImages} />
      </div>
      <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">
        <div className="text-text-primary mb-2 text-lg font-semibold">
          {t('dashboard.products.options.title')}
        </div>
        <Options
          options={options}
          setOptions={setOptions}
          errors={errors.options}
          maxProductVariants={maxProductVariants}
          setErrors={setErrors}
        />
      </div>
      {!!variants.length && (
        <div className="bg-brand-primary-subtle m-6 rounded-lg p-6">
          <div className="text-text-primary mb-2 text-lg font-semibold">
            {t('dashboard.products.variants.title')}
          </div>
          <Variants
            allowStock={limitStock}
            stock={stock ?? 0}
            variants={variants}
            setVariants={setVariants}
            errors={productVariantsErrors}
            setErrors={setProductVariantsErrors}
          />
        </div>
      )}
      {errors.common?.map((error, index) => (
        <p className="text-danger px-6 text-sm" key={index}>
          {error}
        </p>
      ))}
      {validationErrorsNotification && (
        <p className="text-danger ml-auto px-6 text-sm">
          {t('product.validation.notification.title')}
        </p>
      )}
      <div className="flex justify-end gap-4 p-6">
        <SecondaryButton as={LinkWithoutPrefetch} href={'/products'} width="large">
          {t('confirmation_modal.cancel')}
        </SecondaryButton>
        <PrimaryButton width="large" onClick={handleSave} isFetching={isFetching}>
          {t('global.save')}
        </PrimaryButton>
      </div>
    </div>
  )
}

export default ProductForm
